import { m } from '@sohanscript/i18n';
import type { Service, ServiceCategory } from './services';
import { getService, services } from './services';

type MessageKey = keyof typeof m;
type PlainMessage = Extract<(typeof m)[MessageKey], () => string>;

const t = (key: MessageKey) => (m[key] as unknown as PlainMessage)();

const chipKey = (tag: string) =>
	`svchip_${tag.toLowerCase().replace(/[^a-z0-9]+/g, '_')}` as MessageKey;

const ctagKey = (tag: string) =>
	`svtag_${tag
		.replace('#', '')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '_')}` as MessageKey;

const grpKey = (group: string) =>
	`sgrp_${group.toLowerCase().replace(/\s+/g, '_')}` as MessageKey;

const procKey = (step: string) => `proc_${step.toLowerCase()}` as MessageKey;

const svcKey = (slug: string, segment: string) =>
	`svc_${slug}_${segment}` as MessageKey;

export const categoryLabels: Record<ServiceCategory, () => string> = {
	AI: () => m.svcat_ai(),
	Mobile: () => m.svcat_mobile(),
	Automation: () => m.svcat_automation(),
	Data: () => m.svcat_data(),
	Backend: () => m.svcat_backend(),
	'Full-Stack': () => m.svcat_fullstack(),
	Extensions: () => m.svcat_extensions(),
};

export function localizeService(service: Service): Service {
	return {
		...service,
		categoryLabel: t(svcKey(service.slug, 'cat')),
		title: t(svcKey(service.slug, 'title')),
		tagline: t(svcKey(service.slug, 'tagline')),
		stackGroups: service.stackGroups.map((group) => ({
			...group,
			group: t(grpKey(group.group)),
		})),
		scope: service.scope.map((item) => ({
			...item,
			categoryTag: t(ctagKey(item.categoryTag)),
			title: t(svcKey(service.slug, `${item.id}_title`)),
			description: t(svcKey(service.slug, `${item.id}_desc`)),
			tags: item.tags.map((tag) => (chipKey(tag) in m ? t(chipKey(tag)) : tag)),
		})),
		process: service.process.map((step) => ({
			...step,
			step: t(procKey(step.step)),
			detail: t(svcKey(service.slug, `${step.step}_detail`)),
			output: t(svcKey(service.slug, `${step.step}_output`)),
		})),
		telemetry: service.telemetry.map((tlm, i) => ({
			...tlm,
			key: t(svcKey(service.slug, `tlm_${i}`)),
		})),
		relatedWork: service.relatedWork?.map((work, i) => ({
			...work,
			badge: t(svcKey(service.slug, `work_${i}_badge`)),
			description: t(svcKey(service.slug, `work_${i}_desc`)),
			metrics: work.metrics.map((metric, j) => ({
				...metric,
				label: t(svcKey(service.slug, `work_${i}_metric_${j}_label`)),
			})),
		})),
	};
}

export function localizeServices(): Service[] {
	return services.map(localizeService);
}

export function localizeServiceBySlug(slug: string): Service | undefined {
	const service = getService(slug);
	return service ? localizeService(service) : undefined;
}
